import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import Button from './ui/Button';
import type {
  AdminAdvertiser,
  AdminCampaign,
  AdminCreative,
  SponsorshipReport,
} from '../services/api/sponsorship';
import { resolvePublicFileUrl } from '../utils/imageUrls';

export const AdminSponsorship: React.FC = () => {
  const [advertisers, setAdvertisers] = useState<AdminAdvertiser[]>([]);
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [creatives, setCreatives] = useState<AdminCreative[]>([]);
  const [report, setReport] = useState<SponsorshipReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const [advertiserName, setAdvertiserName] = useState('');
  const [campaignName, setCampaignName] = useState('');
  const [campaignAdvertiserId, setCampaignAdvertiserId] = useState<number | ''>('');
  const [campaignPlacement, setCampaignPlacement] = useState('homepage_banner');

  const [creativeCampaignId, setCreativeCampaignId] = useState<number | ''>('');
  const [headline, setHeadline] = useState('');
  const [body, setBody] = useState('');
  const [ctaLabel, setCtaLabel] = useState('View details');
  const [clickUrl, setClickUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ads, camps, creats, rep] = await Promise.all([
        apiService.adminListAdvertisers().catch(() => []),
        apiService.adminListCampaigns().catch(() => []),
        apiService.adminListCreatives().catch(() => []),
        apiService.adminSponsorshipReport().catch(() => null),
      ]);
      setAdvertisers(ads);
      setCampaigns(camps);
      setCreatives(creats);
      setReport(rep);
      if (ads[0] && campaignAdvertiserId === '') setCampaignAdvertiserId(ads[0].id);
      if (camps[0] && creativeCampaignId === '') setCreativeCampaignId(camps[0].id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load sponsorship data');
    } finally {
      setLoading(false);
    }
  }, [campaignAdvertiserId, creativeCampaignId]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const createAdvertiser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!advertiserName.trim()) return;
    setSaving(true);
    setStatusMsg(null);
    try {
      await apiService.adminCreateAdvertiser(advertiserName.trim());
      setAdvertiserName('');
      setStatusMsg('Advertiser created.');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create advertiser');
    } finally {
      setSaving(false);
    }
  };

  const createCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!campaignName.trim() || !campaignAdvertiserId) return;
    setSaving(true);
    try {
      await apiService.adminCreateCampaign({
        advertiser_id: Number(campaignAdvertiserId),
        name: campaignName.trim(),
        placement: campaignPlacement,
        status: 'active',
      });
      setCampaignName('');
      setStatusMsg('Campaign created.');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const createCreative = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!creativeCampaignId || !headline.trim()) {
      setError('Campaign and headline are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('resource', 'creative');
      form.append('campaign_id', String(creativeCampaignId));
      form.append('headline', headline.trim());
      form.append('body', body);
      form.append('cta_label', ctaLabel);
      form.append('click_url', clickUrl);
      form.append('placement', campaignPlacement);
      if (imageFile) form.append('image', imageFile);
      await apiService.adminCreateCreative(form);
      setHeadline('');
      setBody('');
      setClickUrl('');
      setImageFile(null);
      setStatusMsg('Creative uploaded.');
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create creative');
    } finally {
      setSaving(false);
    }
  };

  const toggleCampaign = async (c: AdminCampaign) => {
    const next = c.status === 'active' ? 'paused' : 'active';
    try {
      await apiService.adminUpdateCampaign(c.id, { status: next });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update campaign');
    }
  };

  const toggleCreative = async (c: AdminCreative) => {
    const id = c.id || c.creative_id;
    const next = c.status === 'active' ? 'paused' : 'active';
    try {
      await apiService.adminUpdateCreative(id, { status: next });
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update creative');
    }
  };

  return (
    <div className="space-y-8 max-w-5xl">
      <div>
        <PageHeader eyebrow="Admin" title="Sponsorship" description="First-party campaigns and creatives." />
        <p className="text-gray-400 text-sm mt-1">
          Advertisers, campaigns, and creatives. Paid units are always labeled Sponsored.
        </p>
      </div>

      {error ? (
        <div role="alert" className="rounded-lg border border-red-800 bg-red-950/30 p-4 text-red-300 text-sm">
          {error}
        </div>
      ) : null}
      {statusMsg ? (
        <p role="status" className="text-green-300 text-sm">
          {statusMsg}
        </p>
      ) : null}

      {report ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div className="border border-white/[0.08] rounded-lg px-4 py-3">
            <p className="text-gray-500 text-xs uppercase tracking-wide">Impressions</p>
            <p className="text-white text-2xl font-semibold tabular-nums">{report.impressions ?? 0}</p>
          </div>
          <div className="border border-white/[0.08] rounded-lg px-4 py-3">
            <p className="text-gray-500 text-xs uppercase tracking-wide">Clicks</p>
            <p className="text-white text-2xl font-semibold tabular-nums">{report.clicks ?? 0}</p>
          </div>
        </div>
      ) : null}

      {loading ? <p className="text-gray-400 text-sm">Loading…</p> : null}

      <form onSubmit={createAdvertiser} className="border border-white/[0.08] rounded-xl p-4 space-y-3 bg-[#141414]/40">
        <h2 className="text-white font-medium">Add advertiser</h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={advertiserName}
            onChange={(e) => setAdvertiserName(e.target.value)}
            placeholder="Advertiser name"
            className="flex-1 bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
            aria-label="Advertiser name"
          />
          <Button type="submit" variant="secondary" loading={saving}>
            Add
          </Button>
        </div>
        {advertisers.length > 0 ? (
          <ul className="text-sm text-gray-400 space-y-1">
            {advertisers.map((a) => (
              <li key={a.id}>{a.name}</li>
            ))}
          </ul>
        ) : null}
      </form>

      <form onSubmit={createCampaign} className="border border-white/[0.08] rounded-xl p-4 space-y-3 bg-[#141414]/40">
        <h2 className="text-white font-medium">Create campaign</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select
            value={campaignAdvertiserId}
            onChange={(e) => setCampaignAdvertiserId(e.target.value ? Number(e.target.value) : '')}
            className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
            aria-label="Advertiser"
          >
            <option value="">Select advertiser</option>
            {advertisers.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={campaignName}
            onChange={(e) => setCampaignName(e.target.value)}
            placeholder="Campaign name"
            className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
            aria-label="Campaign name"
          />
          <select
            value={campaignPlacement}
            onChange={(e) => setCampaignPlacement(e.target.value)}
            className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
            aria-label="Placement"
          >
            <option value="homepage_banner">Home banner</option>
            <option value="homepage_rail_card">Home rail card</option>
            <option value="discover_native_card">Discover card</option>
            <option value="home_authenticated">Home (signed-in)</option>
            <option value="discover_native_card">Discover native card</option>
          </select>
        </div>
        <Button type="submit" variant="secondary" loading={saving}>
          Create campaign
        </Button>
      </form>

      <div>
        <h2 className="text-white font-medium mb-3">Campaigns</h2>
        {campaigns.length === 0 ? (
          <p className="text-gray-500 text-sm">No campaigns yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/[0.08]">
            <table className="min-w-full text-sm">
              <thead className="bg-[#141414]/80 text-gray-400 text-left">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Advertiser</th>
                  <th className="px-4 py-3">Placement</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Imp / Clicks</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {campaigns.map((c) => (
                  <tr key={c.id}>
                    <td className="px-4 py-3 text-white">{c.name}</td>
                    <td className="px-4 py-3 text-gray-300">{c.advertiser_name || c.advertiser_id}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{c.placement || '-'}</td>
                    <td className="px-4 py-3 text-gray-300">{c.status}</td>
                    <td className="px-4 py-3 text-gray-400 tabular-nums">
                      {c.impressions ?? 0} / {c.clicks ?? 0}
                    </td>
                    <td className="px-4 py-3">
                      <Button type="button" variant="ghost" size="sm" onClick={() => void toggleCampaign(c)}>
                        {c.status === 'active' ? 'Pause' : 'Activate'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <form onSubmit={createCreative} className="border border-white/[0.08] rounded-xl p-4 space-y-3 bg-[#141414]/40">
        <h2 className="text-white font-medium">Upload creative</h2>
        <select
          value={creativeCampaignId}
          onChange={(e) => setCreativeCampaignId(e.target.value ? Number(e.target.value) : '')}
          className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
          aria-label="Campaign for creative"
        >
          <option value="">Select campaign</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="Headline"
          className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
          aria-label="Headline"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Body"
          rows={2}
          className="w-full bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200"
          aria-label="Body"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            type="text"
            value={ctaLabel}
            onChange={(e) => setCtaLabel(e.target.value)}
            placeholder="CTA label"
            className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
            aria-label="CTA label"
          />
          <input
            type="url"
            value={clickUrl}
            onChange={(e) => setClickUrl(e.target.value)}
            placeholder="Destination URL (wrapped via ad_click)"
            className="bg-[#0a0a0a] border border-white/[0.08] rounded-lg px-3 py-2 text-gray-200 min-h-[44px]"
            aria-label="Destination URL"
          />
        </div>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setImageFile(e.target.files?.[0] || null)}
          className="block w-full text-sm text-gray-400"
          aria-label="Creative image"
        />
        <Button type="submit" variant="primary" loading={saving}>
          Upload creative
        </Button>
      </form>

      <div>
        <h2 className="text-white font-medium mb-3">Creatives preview</h2>
        {creatives.length === 0 ? (
          <p className="text-gray-500 text-sm">No creatives yet.</p>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {creatives.map((c) => {
              const img = resolvePublicFileUrl(c.image_url);
              const id = c.id || c.creative_id;
              return (
                <li key={id} className="border border-white/[0.08] rounded-lg overflow-hidden bg-[#0c0c0c]/50">
                  <p className="px-3 pt-3 text-[11px] uppercase tracking-wide text-gray-500">
                    Sponsored · {c.advertiser_name}
                  </p>
                  {img ? (
                    <img src={img} alt={c.alt_text || c.headline} className="w-full aspect-[16/9] object-cover mt-2" />
                  ) : null}
                  <div className="p-3">
                    <p className="text-white font-medium text-sm">{c.headline}</p>
                    {c.body ? <p className="text-gray-400 text-xs mt-1 line-clamp-2">{c.body}</p> : null}
                    <div className="mt-3 flex items-center justify-between gap-2">
                      <span className="text-xs text-gray-500">{c.status || 'active'}</span>
                      <Button type="button" variant="ghost" size="sm" onClick={() => void toggleCreative(c)}>
                        {c.status === 'paused' ? 'Activate' : 'Pause'}
                      </Button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default AdminSponsorship;
